// src/components/AppHeader.js
import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Divider,
  Box,
  Avatar,
  Collapse,
  Badge,
  Tooltip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  History as HistoryIcon,
  People as PeopleIcon,
  EventAvailable as AttendanceIcon,
  ReportProblem as WarningIcon,
  Assignment as ApprovalIcon,
  Download as T2HRDownloadIcon,
  ExpandLess,
  ExpandMore,
  Settings as SettingsIcon,
  LocationOn as LocationIcon,
  Router as BeaconIcon,
  AccountTree as WorkflowIcon,
  Assessment as ReportsIcon,
  Description as ReportDocIcon,
  Dashboard as DashboardIcon,
  Schedule as ScheduleIcon,
  EventNote as HolidayIcon,
  Loop as PatternIcon,
  Groups as GroupsIcon,
  Map as MapIcon,
  GroupWork as GroupWorkIcon,
  SwapHoriz as SwapIcon,
  EventNote as EventIcon,
  AccountBalance as AccountBalanceIcon,
  Badge as BadgeIcon,
  AdminPanelSettings as AdminIcon,
  Security as SecurityIcon,
  FormatListBulleted as ListIcon,
  Gavel as GavelIcon,
  AccessTime as AccessTimeIcon,
  PlayCircleOutline as RunIcon,
  VisibilityOutlined as ViewIcon,
  Work as WorkIcon,
  AssignmentInd as DocumentIcon,
  Policy as PolicyIcon,
  Payment as PayIcon,
  Campaign as CampaignIcon,
  Backup as BackupIcon,
  CalendarMonth as CalendarMonthIcon,
  Storefront as StoreIcon,
  Explore as FieldOpsIcon,
} from "@mui/icons-material";

import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useTenantContext } from "../context/TenantContext";
import packageJson from "../../package.json";

// Helper for initials
const getInitials = (name) => {
  if (!name) return "U";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const AppHeader = ({ setIsLoggedIn }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const { tenantProfile } = useTenantContext();

  // --- STATE ---
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [smOpen, setSmOpen] = useState(false);
  const [fieldOpsOpen, setFieldOpsOpen] = useState(
    location.pathname.startsWith("/field-ops") ||
    location.pathname.startsWith("/sm/route-map") ||
    location.pathname.startsWith("/sm/stores")
  );
  const [hrOpen, setHrOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [payrollOpen, setPayrollOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const [role, setRole] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [userDisplay, setUserDisplay] = useState("");
  const [tenantDisplay, setTenantDisplay] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");

  const hasPermission = (perm) => permissions.includes(perm);
  const hasAnyPermission = (...perms) => perms.some((perm) => hasPermission(perm));

  const userRole = role?.toLowerCase();
  const isSuperAdmin = userRole === "super_admin" || userRole === "support_admin";
  const isStaffAllowed = hasAnyPermission("MANAGE_STAFF", "VIEW_STAFF") || isSuperAdmin || userRole === "hr_manager";
  const isLeaveAllowed = hasAnyPermission("MANAGE_LEAVE", "VIEW_LEAVE") || isSuperAdmin;
  const isLeaveManageAllowed = hasPermission("MANAGE_LEAVE") || isSuperAdmin;
  const isHR = isStaffAllowed || isLeaveAllowed || hasAnyPermission("ACCESS_PAYROLL", "VIEW_PAYROLL", "VIEW_ATTENDANCE", "MANAGE_ONBOARDING", "VIEW_REPORTS") || isSuperAdmin || userRole === "hr_manager";
  const isSOMAllowed = hasAnyPermission("ACCESS_SOM", "VIEW_ORGANIZATION") || isSuperAdmin;
  const isAttendanceAllowed = hasAnyPermission("VIEW_ATTENDANCE", "MANAGE_ATTENDANCE") || isSuperAdmin || userRole === "hr_manager" || userRole === "hr";
  const isPayrollAllowed = hasAnyPermission("ACCESS_PAYROLL", "VIEW_PAYROLL") || isSuperAdmin || userRole === "hr_manager" || userRole === "management_viewer";
  const isPayrollManageAllowed = hasPermission("ACCESS_PAYROLL") || isSuperAdmin || userRole === "hr_manager";
  const isSMAllowed = hasAnyPermission("MANAGE_SHIFTS", "VIEW_SHIFTS") || isSuperAdmin;
  const isSMManageAllowed = hasPermission("MANAGE_SHIFTS") || isSuperAdmin;
  const isFieldOpsAllowed = isSMAllowed || isSuperAdmin || userRole === "hr_manager" || userRole === "manager";
  const isSetupAllowed = hasPermission("SYSTEM_SETUP") || isSuperAdmin;
  const isSecurityAllowed = hasPermission("SECURITY_ACCESS") || isSuperAdmin;
  const isAccountsAllowed = hasPermission("SYSTEM_ACCOUNTS") || isSuperAdmin;

  // --- DRAWER TOGGLE ---
  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  // --- NAVIGATION HELPER ---
  const handleNavigate = (path) => {
    navigate(path);
    setDrawerOpen(false); // Close drawer on mobile/tablet after navigation
  };

  // --- LOGOUT ---
  const handleLogout = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/web-logout`,
        {},
        {
          withCredentials: true,
          headers: {
            "x-tenant": localStorage.getItem("tenant") || "",
          },
        }
      );
    } catch (err) {
      console.error("Logout API Error:", err);
    }

    sessionStorage.removeItem("user"); // clear user session
    sessionStorage.removeItem("session_valid");
    if (setIsLoggedIn) {
      setIsLoggedIn(false);
    }
    window.location.href = "/login";
  };

  // --- DATA FETCHING ---
  const fetchPendingCount = async () => {
    try {
      const tenant = localStorage.getItem("tenant") || "";
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL1}/approval/inbox/count`,
        {
          withCredentials: true,
          headers: { "X-Tenant": tenant },
        }
      );
      if (res.data?.success) setPendingCount(res.data.count || 0);
    } catch (err) {
      console.error("Fetch pending count error:", err);
    }
  };

  useEffect(() => {
    fetchPendingCount();
    const timer = setInterval(fetchPendingCount, 60000); // Poll every minute
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const storedUser = sessionStorage.getItem("user");
    const tenant = localStorage.getItem("tenant") || "";
    setTenantDisplay(tenant);

    if (storedUser) {
      const u = JSON.parse(storedUser);
      setRole(u.role);
      setPermissions(u.permissions || []);
      const name =
        u.full_name || u.fullName || u.username || u.email || u.name || "";
      setUserDisplay(name);
      return;
    }

    // Fallback: restore session from backend
    const restoreSession = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/me`, {
          withCredentials: true,
          headers: { "X-Tenant": tenant },
        });

        if (res.data?.success && res.data.user) {
          sessionStorage.setItem("user", JSON.stringify(res.data.user));
          setRole(res.data.user.role);
          setPermissions(res.data.user.permissions || []);

          const u = res.data.user;
          const name =
            u.full_name || u.fullName || u.username || u.email || u.name || "";
          setUserDisplay(name);
          setPhotoUrl(u.photo_url || "");

          const tenantNow = localStorage.getItem("tenant") || "";
          setTenantDisplay(tenantNow);
        }
      } catch (err) {
        console.warn("Session restore failed:", err);
      }
    };

    const isPublicPage = window.location.pathname === "/login" || window.location.pathname === "/reset-password";
    if (!sessionStorage.getItem("user") && !isPublicPage) {
      restoreSession();
    }
  }, []);

  useEffect(() => {
    const storedUser = sessionStorage.getItem("user");
    if (storedUser) {
      const u = JSON.parse(storedUser);
      setPhotoUrl(u.photo_url || "");
    }
  }, [location]);

  // --- DRAWER CONTENT ---
  const drawerContent = (
    <Box
      sx={{
        width: 280,
        height: "100%",
        bgcolor: "#f9f9f9",
        display: "flex",
        flexDirection: "column",
      }}
      role="presentation"
    >
      {/* Drawer Header: User Info */}
      <Box
        sx={{
          p: 3,
          background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.main}dd 100%)`,
          color: "#fff",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Avatar
          src={photoUrl || "/default-avatar.png"}
          sx={{
            width: 64,
            height: 64,
            bgcolor: "#fff",
            color: "primary.main",
            fontWeight: "bold",
            fontSize: "1.5rem",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          }}
          imgProps={{
            style: {
              objectFit: "cover",
              objectPosition: "top center",
            },
          }}
        >
          {getInitials(userDisplay)}
        </Avatar>
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {userDisplay || "Guest"}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.8, display: "block" }}>
            {tenantDisplay ? `Tenant: ${tenantDisplay}` : "No Tenant Selected"}
          </Typography>
        </Box>
      </Box>

      {/* Navigation List */}
      <List sx={{ py: 1, flexGrow: 1, overflowY: "auto" }}>
        <ListItemButton
          selected={location.pathname === "/"}
          onClick={() => handleNavigate("/")}
        >
          <ListItemIcon>
            <DashboardIcon color={location.pathname === "/" ? "primary" : "inherit"} />
          </ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItemButton>

        {isHR && (
          <>
            <Divider sx={{ my: 1, opacity: 0.6 }}>
              <Typography
                variant="caption"
                sx={{ px: 2, color: "text.secondary", fontWeight: "bold" }}
              >
                MANAGEMENT
              </Typography>
            </Divider>

            {/* Company Management Sub-menu */}
            <ListItemButton onClick={() => setCompanyOpen(!companyOpen)}>
              <ListItemIcon>
                <CampaignIcon color={location.pathname.startsWith("/announcements") ? "primary" : "inherit"} />
              </ListItemIcon>
              <ListItemText primary="Company Management" />
              {companyOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>

            <Collapse in={companyOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemButton
                  sx={{ pl: 4 }}
                  selected={location.pathname === "/announcements"}
                  onClick={() => handleNavigate("/announcements")}
                >
                  <ListItemIcon>
                    <CampaignIcon fontSize="small" color={location.pathname === "/announcements" ? "primary" : "inherit"} />
                  </ListItemIcon>
                  <ListItemText primary="Announcements" />
                </ListItemButton>
              </List>
            </Collapse>

            {/* HR & Organization Sub-menu */}
            <ListItemButton onClick={() => setHrOpen(!hrOpen)}>
              <ListItemIcon>
                <BadgeIcon color={location.pathname.startsWith("/som") || location.pathname.startsWith("/staff") ? "primary" : "inherit"} />
              </ListItemIcon>
              <ListItemText primary="HR & Organization" />
              {hrOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>

            <Collapse in={hrOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {isSOMAllowed && (
                  <>
                    <ListItemButton
                      sx={{ pl: 4 }}
                      selected={location.pathname === "/som"}
                      onClick={() => handleNavigate("/som")}
                    >
                      <ListItemIcon>
                        <AccountBalanceIcon fontSize="small" color={location.pathname === "/som" ? "primary" : "inherit"} />
                      </ListItemIcon>
                      <ListItemText primary="Org. Master (SOM)" />
                    </ListItemButton>

                    <ListItemButton
                      sx={{ pl: 4 }}
                      selected={location.pathname === "/som/job-catalogue"}
                      onClick={() => handleNavigate("/som/job-catalogue")}
                    >
                      <ListItemIcon>
                        <WorkIcon fontSize="small" color={location.pathname === "/som/job-catalogue" ? "primary" : "inherit"} />
                      </ListItemIcon>
                      <ListItemText primary="Job Catalogue Setup" />
                    </ListItemButton>
                  </>
                )}

                {isStaffAllowed && (
                  <ListItemButton
                    sx={{ pl: 4 }}
                    selected={location.pathname === "/staff" || (location.pathname.startsWith("/staff/") && location.pathname !== "/staff/migrate" && location.pathname !== "/staff/historical-import")}
                    onClick={() => handleNavigate("/staff")}
                  >
                    <ListItemIcon>
                      <PeopleIcon fontSize="small" color={location.pathname === "/staff" || (location.pathname.startsWith("/staff/") && location.pathname !== "/staff/migrate" && location.pathname !== "/staff/historical-import") ? "primary" : "inherit"} />
                    </ListItemIcon>
                    <ListItemText primary="Personnel Records" />
                  </ListItemButton>
                )}

                {hasPermission("EDIT_STAFF") && (
                  <ListItemButton
                    sx={{ pl: 4 }}
                    selected={location.pathname === "/staff/migrate"}
                    onClick={() => handleNavigate("/staff/migrate")}
                  >
                    <ListItemIcon>
                      <BackupIcon fontSize="small" color={location.pathname === "/staff/migrate" ? "primary" : "inherit"} />
                    </ListItemIcon>
                    <ListItemText primary="Staff Import Centre" />
                  </ListItemButton>
                )}

                {hasPermission("EDIT_STAFF") && (
                  <ListItemButton
                    sx={{ pl: 4 }}
                    selected={location.pathname === "/staff/historical-import"}
                    onClick={() => handleNavigate("/staff/historical-import")}
                  >
                    <ListItemIcon>
                      <HistoryIcon fontSize="small" color={location.pathname === "/staff/historical-import" ? "primary" : "inherit"} />
                    </ListItemIcon>
                    <ListItemText primary="Historical Import" />
                  </ListItemButton>
                )}

                {(hasPermission("MANAGE_ONBOARDING") || isSuperAdmin) && (
                  <ListItemButton
                    sx={{ pl: 4 }}
                    selected={location.pathname === "/staff/onboarding"}
                    onClick={() => handleNavigate("/staff/onboarding")}
                  >
                    <ListItemIcon>
                      <DocumentIcon fontSize="small" color={location.pathname === "/staff/onboarding" ? "primary" : "inherit"} />
                    </ListItemIcon>
                    <ListItemText primary="Onboarding Pilot" />
                  </ListItemButton>
                )}
              </List>
            </Collapse>

            {/* Leave Management Sub-menu */}
            {isLeaveAllowed && <ListItemButton onClick={() => setLeaveOpen(!leaveOpen)}>
              <ListItemIcon>
                <EventIcon color={location.pathname.startsWith("/leave") ? "primary" : "inherit"} />
              </ListItemIcon>
              <ListItemText primary="Leave Management" />
              {leaveOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>}

            <Collapse in={isLeaveAllowed && leaveOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemButton
                  sx={{ pl: 4 }}
                  selected={location.pathname === "/leave/manage"}
                  onClick={() => handleNavigate("/leave/manage")}
                >
                  <ListItemIcon>
                    <ListIcon fontSize="small" color={location.pathname === "/leave/manage" ? "primary" : "inherit"} />
                  </ListItemIcon>
                  <ListItemText primary="Leave Records" />
                </ListItemButton>

                <ListItemButton
                  sx={{ pl: 4 }}
                  selected={location.pathname === "/leave/balances"}
                  onClick={() => handleNavigate("/leave/balances")}
                >
                  <ListItemIcon>
                    <AccountBalanceIcon fontSize="small" color={location.pathname === "/leave/balances" ? "primary" : "inherit"} />
                  </ListItemIcon>
                  <ListItemText primary="Leave Balances" />
                </ListItemButton>

                {isLeaveManageAllowed && (
                  <ListItemButton
                    sx={{ pl: 4 }}
                    selected={location.pathname === "/leave/adjustment"}
                    onClick={() => handleNavigate("/leave/adjustment")}
                  >
                    <ListItemIcon>
                      <SwapIcon fontSize="small" color={location.pathname === "/leave/adjustment" ? "primary" : "inherit"} />
                    </ListItemIcon>
                    <ListItemText primary="Leave Adjustment" />
                  </ListItemButton>
                )}

                {isLeaveManageAllowed && (
                  <ListItemButton
                    sx={{ pl: 4 }}
                    selected={location.pathname === "/leave/policies"}
                    onClick={() => handleNavigate("/leave/policies")}
                  >
                    <ListItemIcon>
                      <SettingsIcon fontSize="small" color={location.pathname === "/leave/policies" ? "primary" : "inherit"} />
                    </ListItemIcon>
                    <ListItemText primary="Leave Policies" />
                  </ListItemButton>
                )}

                <ListItemButton
                  sx={{ pl: 4 }}
                  selected={location.pathname === "/reports/leave-calendar"}
                  onClick={() => handleNavigate("/reports/leave-calendar")}
                >
                  <ListItemIcon>
                    <CalendarMonthIcon fontSize="small" color={location.pathname === "/reports/leave-calendar" ? "primary" : "inherit"} />
                  </ListItemIcon>
                  <ListItemText primary="Leave Calendar" />
                </ListItemButton>
              </List>
            </Collapse>

            {/* Shift Management Sub-menu */}
            {isSMAllowed && (
              <>
                <ListItemButton onClick={() => setSmOpen(!smOpen)}>
                  <ListItemIcon>
                    <ScheduleIcon color={location.pathname.startsWith("/sm") ? "primary" : "inherit"} />
                  </ListItemIcon>
                  <ListItemText primary="Shift Management" />
                  {smOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>

                <Collapse in={smOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {isSMManageAllowed && (
                      <>
                        <ListItemButton
                          sx={{ pl: 4 }}
                          selected={location.pathname === "/sm/shifts"}
                          onClick={() => handleNavigate("/sm/shifts")}
                        >
                          <ListItemIcon>
                            <ScheduleIcon color={location.pathname === "/sm/shifts" ? "primary" : "inherit"} />
                          </ListItemIcon>
                          <ListItemText primary="Shift Master" />
                        </ListItemButton>

                        <ListItemButton
                          sx={{ pl: 4 }}
                          selected={location.pathname === "/sm/projects"}
                          onClick={() => handleNavigate("/sm/projects")}
                        >
                          <ListItemIcon>
                            <GroupsIcon color={location.pathname === "/sm/projects" ? "primary" : "inherit"} />
                          </ListItemIcon>
                          <ListItemText primary="Project Setup" />
                        </ListItemButton>
                      </>
                    )}

                    {isSMManageAllowed && (
                      <>
                        <ListItemButton
                          sx={{ pl: 4 }}
                          selected={location.pathname === "/sm/holidays"}
                          onClick={() => handleNavigate("/sm/holidays")}
                        >
                          <ListItemIcon>
                            <HolidayIcon color={location.pathname === "/sm/holidays" ? "primary" : "inherit"} />
                          </ListItemIcon>
                          <ListItemText primary="Holiday Calendar" />
                        </ListItemButton>

                        <ListItemButton
                          sx={{ pl: 4 }}
                          selected={location.pathname === "/sm/patterns"}
                          onClick={() => handleNavigate("/sm/patterns")}
                        >
                          <ListItemIcon>
                            <PatternIcon color={location.pathname === "/sm/patterns" ? "primary" : "inherit"} />
                          </ListItemIcon>
                          <ListItemText primary="Work Patterns" />
                        </ListItemButton>

                        <ListItemButton
                          sx={{ pl: 4 }}
                          selected={location.pathname === "/sm/groups"}
                          onClick={() => handleNavigate("/sm/groups")}
                        >
                          <ListItemIcon>
                            <GroupsIcon color={location.pathname === "/sm/groups" ? "primary" : "inherit"} />
                          </ListItemIcon>
                          <ListItemText primary="Staff Groups" />
                        </ListItemButton>
                      </>
                    )}

                    {isSMManageAllowed && (
                      <ListItemButton
                        sx={{ pl: 4 }}
                        selected={location.pathname === "/sm/exceptions"}
                        onClick={() => handleNavigate("/sm/exceptions")}
                      >
                        <ListItemIcon>
                          <SwapIcon color={location.pathname === "/sm/exceptions" ? "primary" : "inherit"} />
                        </ListItemIcon>
                        <ListItemText primary="Exception Manager" />
                      </ListItemButton>
                    )}

                    {isSMManageAllowed && (
                      <ListItemButton
                        sx={{ pl: 4 }}
                        selected={location.pathname === "/sm/late-policies"}
                        onClick={() => handleNavigate("/sm/late-policies")}
                      >
                        <ListItemIcon>
                          <GavelIcon color={location.pathname === "/sm/late-policies" ? "primary" : "inherit"} />
                        </ListItemIcon>
                        <ListItemText primary="Late Policy Rules" />
                      </ListItemButton>
                    )}

                    {isSMManageAllowed && (
                      <ListItemButton
                        sx={{ pl: 4 }}
                        selected={location.pathname === "/sm/attendance-penalty"}
                        onClick={() => handleNavigate("/sm/attendance-penalty")}
                      >
                        <ListItemIcon>
                          <GavelIcon color={location.pathname === "/sm/attendance-penalty" ? "primary" : "inherit"} />
                        </ListItemIcon>
                        <ListItemText primary="Attendance Penalty Policy" />
                      </ListItemButton>
                    )}

                    {isSMManageAllowed && (
                      <ListItemButton
                        sx={{ pl: 4 }}
                        selected={location.pathname === "/sm/ot-policies"}
                        onClick={() => handleNavigate("/sm/ot-policies")}
                      >
                        <ListItemIcon>
                          <AccessTimeIcon color={location.pathname === "/sm/ot-policies" ? "primary" : "inherit"} />
                        </ListItemIcon>
                        <ListItemText primary="OT Policy Rules" />
                      </ListItemButton>
                    )}

                    {isSMManageAllowed && (
                      <ListItemButton
                        sx={{ pl: 4 }}
                        selected={location.pathname === "/sm/allowance-policies"}
                        onClick={() => handleNavigate("/sm/allowance-policies")}
                      >
                        <ListItemIcon>
                          <PolicyIcon color={location.pathname === "/sm/allowance-policies" ? "primary" : "inherit"} />
                        </ListItemIcon>
                        <ListItemText primary="Allowance Policy Engine" />
                      </ListItemButton>
                    )}

                    <ListItemButton
                      sx={{ pl: 4 }}
                      selected={location.pathname === "/sm/roster"}
                      onClick={() => handleNavigate("/sm/roster")}
                    >
                      <ListItemIcon>
                        <EventIcon color={location.pathname === "/sm/roster" ? "primary" : "inherit"} />
                      </ListItemIcon>
                      <ListItemText primary="Roster Planner" />
                    </ListItemButton>
                  </List>
                </Collapse>
              </>
            )}

            {/* Field Operations Sub-menu */}
            {isFieldOpsAllowed && (
              <>
                <ListItemButton onClick={() => setFieldOpsOpen(!fieldOpsOpen)}>
                  <ListItemIcon>
                    <FieldOpsIcon color={location.pathname.startsWith("/field-ops") || location.pathname.startsWith("/sm/route-map") || location.pathname.startsWith("/sm/stores") ? "primary" : "inherit"} />
                  </ListItemIcon>
                  <ListItemText primary="Field Operations" />
                  {fieldOpsOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>

                <Collapse in={fieldOpsOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItemButton
                      sx={{ pl: 4 }}
                      selected={location.pathname === "/field-ops/stores" || location.pathname === "/sm/stores"}
                      onClick={() => handleNavigate("/field-ops/stores")}
                    >
                      <ListItemIcon>
                        <StoreIcon color={location.pathname === "/field-ops/stores" || location.pathname === "/sm/stores" ? "primary" : "inherit"} />
                      </ListItemIcon>
                      <ListItemText primary="Store / Outlet Setup" />
                    </ListItemButton>

                    <ListItemButton
                      sx={{ pl: 4 }}
                      selected={location.pathname === "/field-ops/route-map" || location.pathname === "/sm/route-map"}
                      onClick={() => handleNavigate("/field-ops/route-map")}
                    >
                      <ListItemIcon>
                        <MapIcon color={location.pathname === "/field-ops/route-map" || location.pathname === "/sm/route-map" ? "primary" : "inherit"} />
                      </ListItemIcon>
                      <ListItemText primary="Field Route Map" />
                    </ListItemButton>
                  </List>
                </Collapse>
              </>
            )}

            {/* Payroll Management Sub-menu */}
            {isPayrollAllowed && (
              <>
                <ListItemButton onClick={() => setPayrollOpen(!payrollOpen)}>
                  <ListItemIcon>
                    <AccountBalanceIcon color={location.pathname.startsWith("/payroll") ? "primary" : "inherit"} />
                  </ListItemIcon>
                  <ListItemText primary="Payroll & Compensation" />
                  {payrollOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>

                <Collapse in={payrollOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItemButton
                      sx={{ pl: 4 }}
                      selected={location.pathname === "/payroll/dashboard" || location.pathname === "/payroll"}
                      onClick={() => handleNavigate("/payroll/dashboard")}
                    >
                      <ListItemIcon>
                        <DashboardIcon fontSize="small" color={location.pathname === "/payroll/dashboard" || location.pathname === "/payroll" ? "primary" : "inherit"} />
                      </ListItemIcon>
                      <ListItemText primary="Payroll Dashboard" />
                    </ListItemButton>

                    {isPayrollManageAllowed && (
                      <>
                        <ListSubheader disableSticky sx={{ pl: 4, lineHeight: '32px', bgcolor: 'transparent', color: '#94a3b8', fontWeight: 800, fontSize: '0.68rem' }}>PAYROLL SETUP</ListSubheader>

                        <ListItemButton
                          sx={{ pl: 4 }}
                          selected={location.pathname === "/payroll/pending-setups"}
                          onClick={() => handleNavigate("/payroll/pending-setups")}
                        >
                          <ListItemIcon>
                            <BadgeIcon fontSize="small" color={location.pathname === "/payroll/pending-setups" ? "primary" : "inherit"} />
                          </ListItemIcon>
                          <ListItemText primary="Pending Payroll Setups" />
                        </ListItemButton>

                        <ListItemButton
                          sx={{ pl: 4 }}
                          selected={location.pathname === "/payroll/settings"}
                          onClick={() => handleNavigate("/payroll/settings")}
                        >
                          <ListItemIcon>
                            <SettingsIcon fontSize="small" color={location.pathname === "/payroll/settings" ? "primary" : "inherit"} />
                          </ListItemIcon>
                          <ListItemText primary="Payroll Settings" />
                        </ListItemButton>

                        <ListItemButton
                          sx={{ pl: 4 }}
                          selected={location.pathname === "/payroll/groups"}
                          onClick={() => handleNavigate("/payroll/groups")}
                        >
                          <ListItemIcon>
                            <GroupWorkIcon fontSize="small" color={location.pathname === "/payroll/groups" ? "primary" : "inherit"} />
                          </ListItemIcon>
                          <ListItemText primary="Payroll Groups" />
                        </ListItemButton>

                        <ListItemButton
                          sx={{ pl: 4 }}
                          selected={location.pathname === "/payroll/group-rules"}
                          onClick={() => handleNavigate("/payroll/group-rules")}
                        >
                          <ListItemIcon>
                            <PolicyIcon fontSize="small" color={location.pathname === "/payroll/group-rules" ? "primary" : "inherit"} />
                          </ListItemIcon>
                          <ListItemText primary="Group Payment Rules" />
                        </ListItemButton>

                        <ListItemButton
                          sx={{ pl: 4 }}
                          selected={location.pathname === "/payroll/financial-years"}
                          onClick={() => handleNavigate("/payroll/financial-years")}
                        >
                          <ListItemIcon>
                            <HolidayIcon fontSize="small" color={location.pathname === "/payroll/financial-years" ? "primary" : "inherit"} />
                          </ListItemIcon>
                          <ListItemText primary="Financial Years" />
                        </ListItemButton>
                        <ListItemButton
                          sx={{ pl: 4 }}
                          selected={location.pathname === "/payroll/calc-bases"}
                          onClick={() => handleNavigate("/payroll/calc-bases")}
                        >
                          <ListItemIcon>
                            <SettingsIcon fontSize="small" color={location.pathname === "/payroll/calc-bases" ? "primary" : "inherit"} />
                          </ListItemIcon>
                          <ListItemText primary="Calculation Bases" />
                        </ListItemButton>

                        <ListItemButton
                          sx={{ pl: 4 }}
                          selected={location.pathname === "/payroll/elements"}
                          onClick={() => handleNavigate("/payroll/elements")}
                        >
                          <ListItemIcon>
                            <ListIcon fontSize="small" color={location.pathname === "/payroll/elements" ? "primary" : "inherit"} />
                          </ListItemIcon>
                          <ListItemText primary="Pay Elements" />
                        </ListItemButton>

                        <ListItemButton
                          sx={{ pl: 4 }}
                          selected={location.pathname === "/payroll/cost-centres"}
                          onClick={() => handleNavigate("/payroll/cost-centres")}
                        >
                          <ListItemIcon>
                            <SettingsIcon fontSize="small" color={location.pathname === "/payroll/cost-centres" ? "primary" : "inherit"} />
                          </ListItemIcon>
                          <ListItemText primary="Cost Centres" />
                        </ListItemButton>

                        <ListItemButton
                          sx={{ pl: 4 }}
                          selected={location.pathname === "/payroll/accounting-map"}
                          onClick={() => handleNavigate("/payroll/accounting-map")}
                        >
                          <ListItemIcon>
                            <SettingsIcon fontSize="small" color={location.pathname === "/payroll/accounting-map" ? "primary" : "inherit"} />
                          </ListItemIcon>
                          <ListItemText primary="Accounting Map" />
                        </ListItemButton>

                        <ListItemButton
                          sx={{ pl: 4 }}
                          selected={location.pathname === "/payroll/opening-balances"}
                          onClick={() => handleNavigate("/payroll/opening-balances")}
                        >
                          <ListItemIcon>
                            <HistoryIcon fontSize="small" color={location.pathname === "/payroll/opening-balances" ? "primary" : "inherit"} />
                          </ListItemIcon>
                          <ListItemText primary="Opening Balances" />
                        </ListItemButton>
                      </>
                    )}

                    <ListSubheader disableSticky sx={{ pl: 4, lineHeight: '32px', bgcolor: 'transparent', color: '#94a3b8', fontWeight: 800, fontSize: '0.68rem' }}>MONTHLY PROCESSING</ListSubheader>

                    <ListItemButton
                      sx={{ pl: 4 }}
                      selected={location.pathname === "/payroll/monthly-review"}
                      onClick={() => handleNavigate("/payroll/monthly-review")}
                    >
                      <ListItemIcon>
                        <ReportsIcon fontSize="small" color={location.pathname === "/payroll/monthly-review" ? "primary" : "inherit"} />
                      </ListItemIcon>
                      <ListItemText primary="Monthly Attendance Summary" />
                    </ListItemButton>

                    {isPayrollManageAllowed && (
                      <ListItemButton
                        sx={{ pl: 4 }}
                        selected={location.pathname === "/payroll/execution"}
                        onClick={() => handleNavigate("/payroll/execution")}
                      >
                        <ListItemIcon>
                          <RunIcon fontSize="small" color={location.pathname === "/payroll/execution" ? "primary" : "inherit"} />
                        </ListItemIcon>
                        <ListItemText primary="Payroll Execution" />
                      </ListItemButton>
                    )}

                    <ListItemButton
                      sx={{ pl: 4 }}
                      selected={location.pathname === "/payroll/draft-review"}
                      onClick={() => handleNavigate("/payroll/draft-review")}
                    >
                      <ListItemIcon>
                        <ViewIcon fontSize="small" color={location.pathname === "/payroll/draft-review" ? "primary" : "inherit"} />
                      </ListItemIcon>
                      <ListItemText primary="Payroll Review & Approval" />
                    </ListItemButton>

                    <ListSubheader disableSticky sx={{ pl: 4, lineHeight: '32px', bgcolor: 'transparent', color: '#94a3b8', fontWeight: 800, fontSize: '0.68rem' }}>OUTPUTS</ListSubheader>

                    <ListItemButton
                      sx={{ pl: 4 }}
                      selected={location.pathname === "/payroll/payslip-distribution"}
                      onClick={() => handleNavigate("/payroll/payslip-distribution")}
                    >
                      <ListItemIcon>
                        <PayIcon fontSize="small" color={location.pathname === "/payroll/payslip-distribution" ? "primary" : "inherit"} />
                      </ListItemIcon>
                      <ListItemText primary="Payslip Distribution" />
                    </ListItemButton>

                    {(hasPermission("PAYROLL_EXPORT") || isSuperAdmin) && (
                      <ListItemButton
                        sx={{ pl: 4 }}
                        selected={location.pathname === "/payroll/account-export"}
                        onClick={() => handleNavigate("/payroll/account-export")}
                      >
                        <ListItemIcon>
                          <T2HRDownloadIcon fontSize="small" color={location.pathname === "/payroll/account-export" ? "primary" : "inherit"} />
                        </ListItemIcon>
                        <ListItemText primary="Account Export" />
                      </ListItemButton>
                    )}
                  </List>
                </Collapse>
              </>
            )}

            {isAttendanceAllowed && (
              <ListItemButton
                selected={location.pathname === "/attendance"}
                onClick={() => handleNavigate("/attendance")}
              >
                <ListItemIcon>
                  <AttendanceIcon color={location.pathname === "/attendance" ? "primary" : "inherit"} />
                </ListItemIcon>
                <ListItemText primary="Attendance Records" />
              </ListItemButton>
            )}

            <ListItemButton
              selected={location.pathname === "/approval/inbox"}
              onClick={() => handleNavigate("/approval/inbox")}
            >
              <ListItemIcon>
                <Badge badgeContent={pendingCount} color="error">
                  <ApprovalIcon color={location.pathname === "/approval/inbox" ? "primary" : "inherit"} />
                </Badge>
              </ListItemIcon>
              <ListItemText primary="Approval Center" />
            </ListItemButton>
          </>
        )}

        <Divider sx={{ my: 1, opacity: 0.6 }}>
          <Typography
            variant="caption"
            sx={{ px: 2, color: "text.secondary", fontWeight: "bold" }}
          >
            EMERGENCY
          </Typography>
        </Divider>

        <ListItemButton
          selected={location.pathname === "/emergency/hot"}
          onClick={() => handleNavigate("/emergency/hot")}
        >
          <ListItemIcon>
            <WarningIcon color="error" />
          </ListItemIcon>
          <ListItemText primary="Hot Events" />
        </ListItemButton>

        <ListItemButton
          selected={location.pathname === "/emergency/hot/history"}
          onClick={() => handleNavigate("/emergency/hot/history")}
        >
          <ListItemIcon>
            <HistoryIcon />
          </ListItemIcon>
          <ListItemText primary="Emergency History" />
        </ListItemButton>

        <Divider sx={{ my: 1, opacity: 0.6 }}>
          <Typography
            variant="caption"
            sx={{ px: 2, color: "text.secondary", fontWeight: "bold" }}
          >
            TOOLS & SYSTEM
          </Typography>
        </Divider>

        {/* SETUP Sub-menu */}
        {isSetupAllowed && (
          <>
            <ListItemButton onClick={() => setSetupOpen(!setupOpen)}>
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary="System Setup" />
              {setupOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={setupOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemButton
                  sx={{ pl: 4 }}
                  selected={location.pathname === "/setup/locations"}
                  onClick={() => handleNavigate("/setup/locations")}
                >
                  <ListItemIcon>
                    <LocationIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Locations" />
                </ListItemButton>
                <ListItemButton
                  sx={{ pl: 4 }}
                  selected={location.pathname === "/setup/beacons"}
                  onClick={() => handleNavigate("/setup/beacons")}
                >
                  <ListItemIcon>
                    <BeaconIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Beacons" />
                </ListItemButton>
                <ListItemButton
                  sx={{ pl: 4 }}
                  selected={location.pathname === "/setup/workflow"}
                  onClick={() => handleNavigate("/setup/workflow")}
                >
                  <ListItemIcon>
                    <WorkflowIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Approval Workflow" />
                </ListItemButton>
                {isAccountsAllowed && (
                  <ListItemButton
                    sx={{ pl: 4 }}
                    selected={location.pathname === "/setup/system-accounts"}
                    onClick={() => handleNavigate("/setup/system-accounts")}
                  >
                    <ListItemIcon>
                      <AdminIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="System Accounts" />
                  </ListItemButton>
                )}
                {isAccountsAllowed && (
                  <ListItemButton
                    sx={{ pl: 4 }}
                    selected={location.pathname === "/setup/branding"}
                    onClick={() => handleNavigate("/setup/branding")}
                  >
                    <ListItemIcon>
                      <SettingsIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Branding Setup" />
                  </ListItemButton>
                )}
                {isSecurityAllowed && (
                  <ListItemButton
                    sx={{ pl: 4 }}
                    selected={location.pathname === "/setup/security"}
                    onClick={() => handleNavigate("/setup/security")}
                  >
                    <ListItemIcon>
                      <SecurityIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Security & Access" />
                  </ListItemButton>
                )}
              </List>
            </Collapse>
          </>
        )}

        {isHR && (
          <>
            {/* REPORTS Sub-menu */}
            <ListItemButton onClick={() => setReportsOpen(!reportsOpen)}>
              <ListItemIcon>
                <ReportsIcon />
              </ListItemIcon>
              <ListItemText primary="Reports" />
              {reportsOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={reportsOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemButton
                  sx={{ pl: 4 }}
                  selected={location.pathname === "/reports"}
                  onClick={() => handleNavigate("/reports")}
                >
                  <ListItemIcon>
                    <DashboardIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Report Dashboard" />
                </ListItemButton>

                <ListItemButton
                  sx={{ pl: 4 }}
                  selected={location.pathname === "/reports/manual-requests"}
                  onClick={() => handleNavigate("/reports/manual-requests")}
                >
                  <ListItemIcon>
                    <ReportDocIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Manual Requests" />
                </ListItemButton>

                <ListItemButton
                  sx={{ pl: 4 }}
                  selected={location.pathname === "/reports/document-expiry"}
                  onClick={() => handleNavigate("/reports/document-expiry")}
                >
                  <ListItemIcon>
                    <DocumentIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Document Expiry" />
                </ListItemButton>

                <ListItemButton
                  sx={{ pl: 4 }}
                  selected={location.pathname === "/reports/leave-calendar"}
                  onClick={() => handleNavigate("/reports/leave-calendar")}
                >
                  <ListItemIcon>
                    <CalendarMonthIcon fontSize="small" color={location.pathname === "/reports/leave-calendar" ? "primary" : "inherit"} />
                  </ListItemIcon>
                  <ListItemText primary="Leave Calendar" />
                </ListItemButton>

                <ListItemButton
                  sx={{ pl: 4 }}
                  selected={location.pathname === "/export/t2hr"}
                  onClick={() => handleNavigate("/export/t2hr")}
                >
                  <ListItemIcon>
                    <T2HRDownloadIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Export" />
                </ListItemButton>
              </List>
            </Collapse>
          </>
        )}
      </List>

      <Divider />
      <ListItemButton onClick={handleLogout} sx={{ mt: 0.5, mb: 0.5 }}>
        <ListItemIcon>
          <LogoutIcon color="action" />
        </ListItemIcon>
        <ListItemText primary="Sign Out" />
      </ListItemButton>
      <Divider />
      <Box sx={{ p: 2, textAlign: "center", bgcolor: "#f1f5f9" }}>
        <Typography variant="caption" display="block" color="text.secondary" fontWeight="medium">
          Version {packageJson.version}
        </Typography>
        <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: "0.68rem", mt: 0.5 }}>
          © {new Date().getFullYear()} Logic Hub Solutions Co., Ltd.
        </Typography>
        <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: "0.68rem" }}>
          All Rights Reserved.
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          bgcolor: "primary.main",
          background: (theme) => `linear-gradient(90deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.light} 100%)`,
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
        }}
      >
        <Toolbar>
          {/* Menu Toggle */}
          <IconButton
            color="inherit"
            edge="start"
            onClick={toggleDrawer(true)}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>

          {/* App Title / Logo */}
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              letterSpacing: "0.5px",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {tenantProfile?.logo_url && tenantProfile.logo_url !== "/logo.png" && (
              <img src={tenantProfile.logo_url} alt="Logo" style={{ height: "32px", marginRight: "8px", objectFit: "contain" }} />
            )}
            {tenantProfile?.tenant_name || "PeopleHub Web Admin"}
          </Typography>

          {/* Desktop User Info */}
          <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 2, mr: 2 }}>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {userDisplay}
            </Typography>
            {tenantDisplay && (
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  bgcolor: "rgba(255,255,255,0.2)",
                  borderRadius: "16px",
                  fontSize: "0.75rem",
                  fontWeight: "bold"
                }}
              >
                {tenantDisplay}
              </Box>
            )}
          </Box>

          {/* Notifications Request Bell */}
          {isHR && (
            <Tooltip title="Check Approvals">
              <IconButton color="inherit" onClick={() => navigate("/approval/inbox")}>
                <Badge badgeContent={pendingCount} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          )}

          {/* Home Button */}
          <Tooltip title="Dashboard">
            <IconButton color="inherit" onClick={() => navigate("/")} sx={{ ml: 1 }}>
              <HomeIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Navigation Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default AppHeader;
